// react
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import type {
  CreateExternalSensorDto,
  ReadExternalSensorDto,
  ReadManufacturerDto,
} from '@/api/generated/recalc/endpoints/v1/externalSensors';
import { useGatewaysGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/gateways';
import {
  useGetExternalSensorManufacturersQuery,
  usePatchExternalSensorMutation,
  usePostExternalSensorMutation,
} from '@/api/recalc/endpoints/externalSensorsApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

type ExternalSensorFormMode = 'create' | 'edit';

interface ExternalSensorFormProps {
  mode: ExternalSensorFormMode;
  externalSensor?: ReadExternalSensorDto | null;
}

const ExternalSensorForm = ({
  mode,
  externalSensor,
}: ExternalSensorFormProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId } = useParams({ strict: false });

  const [postExternalSensor, { isLoading: isCreating }] =
    usePostExternalSensorMutation();
  const [patchExternalSensor, { isLoading: isUpdating }] =
    usePatchExternalSensorMutation();

  const { data: manufacturers = [] } = useGetExternalSensorManufacturersQuery();
  const { data: gateways = [] } =
    useGatewaysGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${accountId})` },
      {
        skip: !accountId,
      },
    );

  const [name, setName] = useState(externalSensor?.Name ?? '');
  const [deviceId, setDeviceId] = useState(externalSensor?.DeviceId ?? '');
  const [gatewayId, setGatewayId] = useState(
    externalSensor?.GatewayId ? String(externalSensor.GatewayId) : '',
  );
  const [encryptionKey, setEncryptionKey] = useState(
    externalSensor?.WmBusDevice?.EncryptionKey ?? '',
  );
  const [selectedManufacturerId, setSelectedManufacturerId] = useState(
    externalSensor?.Device?.Manufacturer?.ManufacturerId
      ? String(externalSensor.Device.Manufacturer.ManufacturerId)
      : '',
  );
  const [selectedManufacturerDeviceKey, setSelectedManufacturerDeviceKey] =
    useState(externalSensor?.Device?.Key ?? '');

  const selectedManufacturer = useMemo(
    () =>
      manufacturers.find(
        (manufacturer) =>
          manufacturer.ManufacturerId === Number(selectedManufacturerId),
      ),
    [manufacturers, selectedManufacturerId],
  );

  const manufacturerDevices =
    selectedManufacturer?.ManufacturerDevices?.slice().sort((left, right) =>
      String(left.Name ?? '').localeCompare(String(right.Name ?? '')),
    ) ?? [];

  const isSubmitting = isCreating || isUpdating;
  const requiresEncryptionKey =
    mode === 'create'
      ? Boolean(selectedManufacturer?.EncryptionKeyRequired)
      : Boolean(externalSensor?.Device?.ExternalSensorType === 'WmBus');

  const handleCancel = () => {
    if (mode === 'edit' && externalSensor?.ExternalSensorId) {
      navigate({
        to: `/$accountId/devices/${externalSensor.ExternalSensorId}`,
      });
      return;
    }

    navigate({ to: '/$accountId/devices' });
  };

  const validate = () => {
    if (!name.trim() || !deviceId.trim()) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.validationRequiredFields'),
          severity: 'error',
        }),
      );
      return false;
    }

    if (mode === 'create' && !selectedManufacturerDeviceKey) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.validationManufacturerDeviceRequired'),
          severity: 'error',
        }),
      );
      return false;
    }

    if (requiresEncryptionKey && !encryptionKey.trim()) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.validationEncryptionKeyRequired'),
          severity: 'error',
        }),
      );
      return false;
    }

    return true;
  };

  const handleCreate = async () => {
    if (!accountId) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.accountMissing'),
          severity: 'error',
        }),
      );
      return;
    }

    const payload: CreateExternalSensorDto = {
      Name: name.trim(),
      Account: {
        AccountId: accountId,
      },
      DeviceId: deviceId.trim(),
      ManufacturerDeviceKey: selectedManufacturerDeviceKey,
      ...(encryptionKey.trim() ? { EncryptionKey: encryptionKey.trim() } : {}),
      ...(gatewayId ? { GatewayId: Number(gatewayId) } : {}),
    };

    const result = await postExternalSensor(payload);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.createdSuccess', {
            name: payload.Name,
          }),
          severity: 'success',
        }),
      );
      navigate({ to: '/$accountId/devices' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleEdit = async () => {
    if (!externalSensor?.ExternalSensorId) {
      return;
    }

    const result = await patchExternalSensor({
      id: externalSensor.ExternalSensorId,
      patch: {
        Name: name.trim(),
        DeviceId: deviceId.trim(),
        ...(encryptionKey.trim()
          ? {
              WmBusDevice: {
                EncryptionKey: encryptionKey.trim(),
              },
            }
          : {}),
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('externalSensors.updatedSuccess', {
            name: name.trim(),
          }),
          severity: 'success',
        }),
      );
      navigate({
        to: `/$accountId/devices/${externalSensor.ExternalSensorId}`,
      });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    if (mode === 'create') {
      await handleCreate();
      return;
    }

    await handleEdit();
  };

  return (
    <Card>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <CardContent>
          <Stack spacing={2}>
            {mode === 'edit' && (
              <Alert severity="info">{t('externalSensors.editInfo')}</Alert>
            )}

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  required
                  fullWidth
                  label={t('externalSensors.name')}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  required
                  fullWidth
                  label={t('externalSensors.deviceId')}
                  value={deviceId}
                  onChange={(event) => setDeviceId(event.target.value)}
                />
              </Grid>

              {mode === 'create' ? (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      select
                      fullWidth
                      label={t('externalSensors.manufacturer')}
                      value={selectedManufacturerId}
                      onChange={(event) => {
                        setSelectedManufacturerId(event.target.value);
                        setSelectedManufacturerDeviceKey('');
                      }}
                    >
                      {manufacturers.map(
                        (manufacturer: ReadManufacturerDto) => (
                          <MenuItem
                            key={manufacturer.ManufacturerId}
                            value={String(manufacturer.ManufacturerId)}
                          >
                            {manufacturer.Name}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      select
                      fullWidth
                      required
                      label={t('externalSensors.manufacturerDevice')}
                      value={selectedManufacturerDeviceKey}
                      onChange={(event) =>
                        setSelectedManufacturerDeviceKey(event.target.value)
                      }
                      disabled={!selectedManufacturerId}
                    >
                      {manufacturerDevices.map((device) => (
                        <MenuItem
                          key={device.ExternalSensorDeviceId}
                          value={device.Key}
                        >
                          {device.Name || device.Key}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      select
                      fullWidth
                      label={t('externalSensors.gateway')}
                      value={gatewayId}
                      onChange={(event) => setGatewayId(event.target.value)}
                    >
                      <MenuItem value="">
                        {t('externalSensors.noGateway')}
                      </MenuItem>
                      {gateways.map((gateway) => (
                        <MenuItem
                          key={gateway.GatewayId}
                          value={String(gateway.GatewayId)}
                        >
                          {gateway.Name || gateway.GatewayId}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      fullWidth
                      label={t('externalSensors.manufacturer')}
                      value={externalSensor?.Device?.Manufacturer?.Name ?? ''}
                      disabled
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      fullWidth
                      label={t('externalSensors.manufacturerDevice')}
                      value={
                        externalSensor?.Device?.Name ||
                        externalSensor?.Device?.Key ||
                        ''
                      }
                      disabled
                    />
                  </Grid>
                </>
              )}

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  required={requiresEncryptionKey}
                  label={t('externalSensors.encryptionKey')}
                  value={encryptionKey}
                  onChange={(event) => setEncryptionKey(event.target.value)}
                  helperText={
                    requiresEncryptionKey
                      ? t('externalSensors.encryptionKeyRequiredHelper')
                      : t('externalSensors.encryptionKeyOptionalHelper')
                  }
                />
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={handleCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            <Typography component="span">
              {mode === 'create'
                ? isSubmitting
                  ? t('externalSensors.creating')
                  : t('externalSensors.create')
                : isSubmitting
                  ? t('externalSensors.updating')
                  : t('common.save')}
            </Typography>
          </Button>
        </CardActions>
      </Box>
    </Card>
  );
};

export default ExternalSensorForm;
